import {
  useEffect,
  useContext,
  createContext,
  useRef,
  useCallback,
} from 'react';
import { useHistory } from 'react-router-dom';

const NavigationContext = createContext();

export const NavigationProvider = ({ children }) => {
  const value = useGenerateNavigation();
  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => useContext(NavigationContext);

const actions = {
  push: 'push',
  pop: 'pop',
};

/**
 * How the browser reached the current document: 'navigate', 'reload',
 * 'back_forward' or 'prerender'. Anything that cannot report is treated as a
 * fresh navigation, which is the safer default for callers deciding whether to
 * discard state.
 * @returns {string}
 */
const getDocumentNavigationType = () => {
  const [entry] = window.performance?.getEntriesByType?.('navigation') ?? [];
  return entry?.type ?? 'navigate';
};

const useGenerateNavigation = () => {
  const { listen } = useHistory();
  const actionRef = useRef(null);

  useEffect(() => {
    return listen((location, action) => {
      // REPLACE rewrites the entry the user is already on, so it says nothing
      // about how they arrived and must leave the recorded action alone.
      if (action === 'PUSH') {
        actionRef.current = actions.push;
      } else if (action === 'POP') {
        actionRef.current = actions.pop;
      }
    });
  }, [listen]);

  /**
   * True when the user arrived somewhere new, false when they reloaded or moved
   * through history in either direction.
   *
   * Navigation Timing describes the document load, so it can only answer until
   * the first client-side route change — reading it afterwards would report every
   * later click on a reloaded page as another reload. Once the router has moved,
   * its own action is the only thing that knows.
   * @returns {boolean}
   */
  const isFreshNavigation = useCallback(() => {
    if (actionRef.current === null) {
      return getDocumentNavigationType() === 'navigate';
    }
    return actionRef.current === actions.push;
  }, []);

  return {
    isFreshNavigation,
  };
};
